import { Injectable } from '@angular/core';
import { Globals } from '../shared/globals';
import { StationType } from '../models/station-type.model';
import { OcppLog } from '../models/ocpp-log.model';
import { Permission } from '../models/permission.model';
import { StationGroup } from '../models/station-group';
import { UserGroup } from '../models/user-group.model';
import {
  JsonObject,
  JsonProperty,
  JsonSerializer,
} from 'typescript-json-serializer';
import { AdminRfidCardData } from '../models/rfidtag.model';
import { AdminCustomStationCode } from '../models/admin-custom-station-code';
import { Api } from '../shared/api';
import { ConfigurationKeyValue, StationSubType } from './maintenance.service';
import { AdminStationDeltaUpdate } from '../models/admin-dtos';
import { StationState } from '../models/station-state.model';

export class AdminStationDto {
  Id: string;
  Name: string;
  UserId: string;
  CreatedAt: string;
  Type: StationType;
  SubType: StationSubType;
  Address: string;
  Connectors: AdminConnectorDto[];
  HubjectEvseIds: string[];
  HubjectPricingPlans: string[];
}

export class AdminConnectorDto {
  Id?: number;
  Position: number;
  Name: string;
  PlugType: string;
  AccessType: string;
  EnergyPrice: number;
  ParkingPrice: number;
  Currency: string;
  State?: StationState;
  SerialNumber: string;
}

export class AdminChargingDto {
  // Metadata
  LastUpdate: Date;

  // This is equal to Device id
  Id: string;
  StationId: string;
  StationName: string;
  DriverId: string;
  DriverName: string;
  ActivatedAt: Date;
  Duration: number;
}

@JsonObject()
export class AdminPriceRuleDto {
  @JsonProperty('Id') id: string;
  @JsonProperty('ProductType') productType: string;
  @JsonProperty('Mode') mode: string;
  @JsonProperty('PriceAssigneeType') priceAssigneeType: string;
  @JsonProperty('PriceAssigneeId') priceAssigneeId: string;
  @JsonProperty('TargetType') targetType: string;
  @JsonProperty('TargetId') targetId: string;
  @JsonProperty('Price') price: number;
  @JsonProperty('Currency') currency: string;
  @JsonProperty('CreatedAt') createdAt: number;
  @JsonProperty('CreatedBy') createdBy: string;

  public toString = () => JSON.stringify(this, null, '  ');
}

@Injectable()
export class AdminService {
  private readonly fetchStationsUrl: string;
  private readonly fetchStationsStatusUrl: string;
  private readonly fetchChargingUrl: string;
  private readonly fetchPermissionsUrl: string;
  private readonly fetchUserGroupsUrl: string;
  private readonly fetchStationGroupsUrl: string;
  private readonly fetchRfidCards: string;
  private readonly priceRulesUrl: string;
  private readonly customStationCodesUrl: string;
  private readonly jsonSerializer: JsonSerializer;

  constructor(private globals: Globals, private api: Api) {
    this.fetchStationsStatusUrl = this.globals.apiUrl('stations');
    this.fetchStationsUrl = this.globals.adminUrl('stations');
    this.fetchChargingUrl = this.globals.adminUrl('charging');
    this.fetchPermissionsUrl = this.globals.adminUrl('permissions');
    this.fetchUserGroupsUrl = this.globals.adminUrl('user-groups');
    this.fetchStationGroupsUrl = this.globals.adminUrl('station-groups');
    this.fetchRfidCards = this.globals.adminUrl('rfid');
    this.priceRulesUrl = this.globals.adminUrl('price-rules');
    this.customStationCodesUrl = this.globals.adminUrl('codes');
    this.jsonSerializer = new JsonSerializer();
  }

  async getStations(): Promise<AdminStationDto[]> {
    return this.api.httpGet(this.fetchStationsUrl);
  }

  async getStationById(stationId: string): Promise<AdminStationDto> {
    const url = `${this.fetchStationsStatusUrl}?id=${stationId}`;
    return this.api.httpGet(url);
  }

  async getConfig(
    stationId: string,
    key: string
  ): Promise<ConfigurationKeyValue> {
    const url = this.getStationUrl(stationId, `configuration?key=${key}`);
    return this.api.httpGet(url);
  }

  async changeConfig(
    stationId: string,
    keyValue: ConfigurationKeyValue
  ): Promise<ConfigurationKeyValue> {
    const url = this.getStationUrl(stationId, 'configuration');
    return this.api.httpPost(url, keyValue);
  }

  async unlockConnector(
    stationId: string,
    connectorId: number
  ): Promise<boolean> {
    const url = this.getStationUrl(
      stationId,
      `unlock?connectorId=${connectorId}`
    );
    return this.api.httpPost(url, null);
  }

  async resetConnector(stationId: string, isSoftReset: boolean): Promise<any> {
    const url = this.getStationUrl(stationId, `reset?soft=${isSoftReset}`);
    return this.api.httpPost(url, '');
  }

  async getRfidCards(): Promise<AdminRfidCardData[]> {
    return this.api.httpGet(this.fetchRfidCards);
  }

  async deleteRfidCard(identification: string): Promise<void> {
    const encodedIdentification = encodeURIComponent(identification);
    const url = this.globals.adminUrl(
      `rfid?identification=${encodedIdentification}`
    );
    return this.api.httpDelete(url);
  }

  async getPriceRules(): Promise<AdminPriceRuleDto[]> {
    const response = await this.api.httpGet(this.priceRulesUrl);
    return this.deserializeArray(response, AdminPriceRuleDto);
  }

  async deletePriceRule(identifier: string): Promise<void> {
    const url = `${this.priceRulesUrl}/${identifier}`;
    return this.api.httpDelete(url);
  }

  async getChargingLists(): Promise<AdminChargingDto[]> {
    return this.api.httpGet(this.fetchChargingUrl);
  }

  async deactivate(id: string): Promise<void> {
    const url = `${this.globals.adminUrl('charging')}/${id}`;
    return this.api.httpDelete(url);
  }

  async remoteStopCharging(id: string): Promise<void> {
    const url = `${this.globals.adminUrl('charging')}/${id}/remote-stop`;
    return this.api.httpPost(url, null);
  }

  async getLogs(stationId: string, from: Date, to: Date): Promise<OcppLog[]> {
    const fromDate = (from.getTime() / 1000).toString();
    // Adding one second to the end time fixes a bizarre bug where there would be no results
    // at all when the cloud queries the database. This is a workaround for a possible
    // bug in the Postgres library the cloud uses to query the database.
    const toDate = (to.getTime() / 1000 + 1).toString();
    const url = this.globals.adminUrl(
      `stations/${stationId}/logs?from=${fromDate}&to=${toDate}`
    );
    return this.api.httpGet(url);
  }

  async getPermissions(): Promise<Permission[]> {
    const response = await this.api.httpGet(this.fetchPermissionsUrl);
    return this.deserializeArray(response, Permission);
  }

  async getUserGroups(): Promise<UserGroup[]> {
    const response = await this.api.httpGet(this.fetchUserGroupsUrl);
    return this.deserializeArray(response, UserGroup);
  }

  async getStationGroups(): Promise<StationGroup[]> {
    const response = await this.api.httpGet(this.fetchStationGroupsUrl);
    return this.deserializeArray(response, StationGroup);
  }

  async getCustomStationCodes(): Promise<AdminCustomStationCode[]> {
    const response = await this.api.httpGet(this.customStationCodesUrl);
    return this.deserializeArray(response, AdminCustomStationCode);
  }

  async deleteCustomStationCode(identifier: string): Promise<void> {
    const url = `${this.customStationCodesUrl}?code=${identifier}`;
    return this.api.httpDelete(url);
  }

  async insertCustomStationCode(code: AdminCustomStationCode): Promise<void> {
    return this.api.httpPost(this.customStationCodesUrl, code);
  }

  async editCustomStationCode(code: AdminCustomStationCode): Promise<void> {
    return this.api.httpPut(this.customStationCodesUrl, code);
  }

  async editStation(
    stationId: string,
    delta: AdminStationDeltaUpdate
  ): Promise<void> {
    const url = this.globals.adminUrl(`stations/${stationId}`);
    return this.api.httpPatch(url, delta);
  }

  async deleteStation(stationId: string): Promise<void> {
    const url = this.globals.adminUrl(`stations/${stationId}`);
    return this.api.httpDelete(url);
  }

  async getDeletedStations(): Promise<string[]> {
    const url = this.globals.adminUrl('deleted-stations');
    return this.api.httpGet(url);
  }

  private getStationUrl(stationId: string, endpoint: string): string {
    return `${this.globals.adminUrl('stations')}/${stationId}/${endpoint}`;
  }

  private deserializeArray<T extends object>(
    response: any,
    type: new () => T
  ): T[] {
    return Object.keys(response).map((item) =>
      this.jsonSerializer.deserializeObject<T>(response[item], type)
    );
  }
}
